/**
 * @file 编辑报名任务全屏弹窗
 */
import { Button, State, Toast } from '@seiue/ui'
import { pick } from '@seiue/util'
import { Form } from 'antd'
import { FC, useEffect, useRef, useState } from 'react'

import {
  BatchReq,
  TaskStatusEnum,
  TaskTypeEnum,
  activityApi,
  waggleApi,
} from 'packages/sdks-next/growp'

import { CommonBasicInfoRef, SubmitValues } from '@/components/CommonBasicInfo'
import { ContentCard } from '@/components/ContentCard'
import { ModalLayout } from '@/components/ModalLayout'
import { TaskHeaderLeft } from '@/components/TaskHeaderLeft'

import { EditTaskModalProps } from '../types'

import { BasicInfo } from './BasicInfo'

/**
 * 编辑报名任务弹窗
 *
 * @param param0 - 参数
 * @param param0.onClose - 关闭弹窗
 * @param param0.taskId - 任务 ID
 * @param param0.activityId - 活动 ID
 * @param param0.activityTaskOptions - 活动任务选项
 * @returns 组件
 */
export const EditWaggleTaskModal: FC<EditTaskModalProps> = ({
  onClose,
  activityId,
  taskId,
  activityTaskOptions,
}) => {
  const isEdit = !!taskId

  const [form] = Form.useForm()
  const [title, setTitle] = useState<string>('')
  const [commonBasicInfo, setCommonBasicInfo] = useState<SubmitValues>({})
  const basicInfoRef = useRef<CommonBasicInfoRef>(null)

  const { data: editTask, loading } = waggleApi.useLoadWaggleActivityTask(
    {
      id: taskId || 0,
    },
    { disable: !taskId }
  )

  const filterTaskOptions =
    activityTaskOptions?.filter((o) => o.value !== taskId) || []

  const { api: createActivityTask } = waggleApi.useCreateWaggleActivityTask({
    reload: [
      activityApi.queryTasks,
      activityApi.loadActivity,
      waggleApi.loadWaggleActivityTask,
    ],
  })

  const { api: updateActivityTask } = waggleApi.useUpdateWaggleActivityTask({
    reload: [
      activityApi.queryTasks,
      activityApi.loadActivity,
      waggleApi.loadWaggleActivityTask,
    ],
  })

  useEffect(() => {
    form.resetFields()

    form.setFieldsValue({
      name: editTask?.task?.name || '',
      batchs: editTask?.batchs ?? [],
    })
    setTitle(editTask?.task?.name || '')

    if (editTask?.task) {
      setCommonBasicInfo({
        ...pick({ ...editTask.task }, [
          'endAt',
          'adminIds',
          'timedStartAt',
          'prevTaskIds',
        ]),
        startAt: null,
        isShow: false,
        endAt: null,
      })
    }
  }, [editTask, form])

  if (loading) return <State.Loading />
  return (
    <ModalLayout
      title={
        <TaskHeaderLeft
          title={title || '未命名摇号'}
          onClose={onClose}
        />
      }
      headerRight={
        <>
          <Button
            type="primary"
            onClick={async () => {
              form.submit()
              const commonRefInfo = basicInfoRef.current?.getBasicInfo()

              form.validateFields().then(async (values) => {
                const batchs = pick(values, ['batchs']).batchs as BatchReq[]
                const task = {
                  ...pick(values, ['name']),
                  ...(commonRefInfo ?? {}),
                  describetion: '',
                  activityId,
                  type: TaskTypeEnum.Waggle,
                  status: TaskStatusEnum.Published,
                  startAt: null,
                  isShow: false,
                  endAt: null,
                }
                if (isEdit) {
                  await updateActivityTask({
                    id: taskId,
                    payload: { task, batchs },
                  })
                } else {
                  await createActivityTask({
                    task,
                    batchs,
                  })
                }

                Toast.success(isEdit ? '更新任务成功' : '保存任务成功')

                onClose()
              })
            }}
          >
            {isEdit ? '更新' : '确认'}
          </Button>
        </>
      }
      content={
        <ContentCard
          style={{ minHeight: 200, width: 960 }}
          contentStyle={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '24px 32px',
          }}
        >
          <BasicInfo
            setTitle={setTitle}
            form={form}
            commonBasicInfo={commonBasicInfo}
            basicInfoRef={basicInfoRef}
            activityTaskOptions={filterTaskOptions}
            initBatchs={editTask?.batchs ?? []}
          />
        </ContentCard>
      }
    />
  )
}
